import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Formik, Form } from "formik";
import { Box, IconButton, Switch, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from 'yup';
import ConfirmationDialog from "../../ConfirmationDialog";
import SelectFieldWithSearch from "../../../../components/Form/SelectFieldWithSearch";
import { setCartDetails, setOrderDetails } from "../../../../Redux/Slices/orderSlice";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { createCartDetails, getCancelReasons, viewOrderDetails } from "../../../../service/order/orderService";
import InvoiceTableRows from "./Fragments/InvoiceTableRows/InvoiceTableRows";
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import { notifyError } from "../../../../components/ToastContainer/Toast/Toast";
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { usePermission } from "../../../../Redux/SlicesHooks/usePermission";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.MuiTableCell-head`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    [`&.MuiTableCell-body`]: {
        fontSize: 14,
    },
}));


const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

const getInitialBilledQuantities = (orderItems: any[]) =>
    orderItems.reduce((acc, item) => ({ ...acc, [item.id]: "" }), {});

const InvoiceOptionsDialog: React.FC<{ params: any }> = ({ params }) => {
    const editOrder = usePermission("orders","update");
    const [open, setOpen] = useState(false);
    const [checked, setChecked] = useState(false);
    const [cancelReasons, setCancelReasons] = useState<CancelReason[]>([]);
    const [selectedReason, setSelectedReason] = useState<string | number>("");
    const [selectedRowId, setSelectedRowId] = useState<string[]>([]);
    const [isProcessEnabled, setIsProcessEnabled] = useState<boolean>(false);
    const [billedQuantities, setBilledQuantities] = useState<Record<string, any>>({});
    const orderDetails = useAppSelector((state) => state.orders.selectedOrderDetails);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const formikValuesRef = useRef<any>({});
    const submitFormRef = useRef<(() => void) | null>(null);
    const { store_id } = useParams();

    const location = useLocation();
    const { triggerId } = (location.state || {}) as {
        triggerId?: number;
        triggerType?: string;
        fromNotification?: boolean;
    }

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const res = await viewOrderDetails(params.row.order_id);
                if (res.status === 200) dispatch(setOrderDetails(res.data.data));
            } catch { }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, params.row?.id, dispatch]);

    useEffect(() => {
        if (!checked) return;
        (async () => {
            try {
                const res = await getCancelReasons();
                if (res.status === 200) {
                    setCancelReasons(res.data.data.map((item: any) => ({
                        label: item?.reason,
                        value: String(item.id),
                    })));
                }
            } catch { }
        })();
    }, [checked]);

    useEffect(() => {
        if (!open) {
            setChecked(false);
            setSelectedReason("");
            setSelectedRowId([]);
            setIsProcessEnabled(false);
            setBilledQuantities({});
        }
    }, [open]);

    useEffect(() => {
        if (params.row.order_id === triggerId) {
            handleActionClick()
            navigate(location.pathname, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.row.order_id, triggerId]);

    useEffect(() => {
        const hasSelectedRows = selectedRowId.length > 0;
        const hasValidQty = selectedRowId.every(
            (id) => billedQuantities[id] !== undefined && billedQuantities[id] !== ""
        );
        setIsProcessEnabled(hasSelectedRows && hasValidQty);
    }, [selectedRowId, billedQuantities]);

    const handleSelectCheck = useCallback((id: string) => {
        setSelectedRowId((prev) =>
            prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
        );
    }, []);

    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            billed_qty: Yup.object(
                selectedRowId.reduce((acc: any, id: string) => {
                    const item = orderDetails.order_items.find((i: any) => i.id === id);
                    if (item) {
                        const remainQty =
                            item.quantity -
                            (item.billed_quantity + item.cancelled_quantity + item.returned_quantity);

                        acc[id] = Yup.number()
                            .typeError("Invalid Number")
                            .integer("Decimals Not Allowed")
                            .required("Process Quantity Required")
                            .min(1, "Enter At least One")
                            .max(remainQty, `Max Allowed Quantity : ${remainQty}`);
                    }
                    return acc;
                }, {})
            ),
            ...(checked && {
                cancelReason: Yup.string().required("Cancel reason required")
            })
        });
    }, [selectedRowId, orderDetails, checked]);

    const handleConfirm = async (values: any) => {
        const selectedCancelReason = cancelReasons.find((item) => item.value.toString() === formikValuesRef.current.cancelReason);
        if (selectedCancelReason) {
            localStorage.setItem("CancelReason", JSON.stringify(selectedCancelReason));
        }
        const items = orderDetails.order_items
            .filter((order: any) => selectedRowId.includes(order.id))
            .map((order: any) => ({
                product_id: String(order.product_id),
                quantity: values.billed_qty[order.id],
            }));
        const cartPayload = {
            items,
            store_id,
            user: {
                name: orderDetails.customer.name,
                email: orderDetails.customer.email,
                phone: orderDetails.customer.phone
            },
            apply: false
        };
        try {
            const response = await createCartDetails(cartPayload);
            if (response.status === 201) {
                dispatch(setCartDetails(response.data));
                localStorage.setItem("cart", JSON.stringify(response.data));
                setOpen(false);
                navigate(location.pathname, { replace: true });
                navigate(`/stores/${store_id}/orders/${params.row.order_id}/${checked ? "cancel-order" : "bill-order"}`);
            }
        } catch {
            setOpen(false);
            navigate(location.pathname, { replace: true });
            notifyError("Failed to Update Cart");
        }
    };

    const handleActionClick = async () => {
        try {
            const res = await viewOrderDetails(params.row.order_id);
            if (res.status === 200) {
                const orderItems = res.data.data.order_items || [];

                const allZeroRemaining = orderItems.length > 0 && orderItems.every(item => {
                    const remainingQty = item.quantity - (item.billed_quantity + item.cancelled_quantity + item.returned_quantity);
                    return remainingQty === 0;
                });

                if (allZeroRemaining) {
                    setOpen(false);
                    localStorage.setItem("modalState", "closed");
                    notifyError("All order items are fully processed");
                    return;
                }
                localStorage.removeItem("modalState");
                setOpen(true);
            }
        } catch {
            notifyError("Something went wrong!");
            setOpen(false);
        }
    };

    return (
        <>
            <Box display="flex" alignItems="center" gap={1} justifyContent="space-between">
                <LightTooltip title="Action" placement="right">
                    <IconButton color="primary" disabled={!editOrder} onClick={() => {
                        handleActionClick();
                    }}>
                        <ShoppingCartCheckoutIcon fontSize="small" />
                    </IconButton>
                </LightTooltip>

            </Box>
            {open && <ConfirmationDialog
                open={open}
                title="Order Details"
                content={orderDetails ? (
                    <Formik
                        enableReinitialize
                        initialValues={{
                            billed_qty: getInitialBilledQuantities(orderDetails?.order_items || []),
                            cancelReason: selectedReason
                        }}
                        validationSchema={validationSchema}
                        validateOnBlur={true}
                        onSubmit={handleConfirm}
                    >
                        {({ values, handleChange, setFieldValue, submitForm, setFieldTouched }) => {
                            submitFormRef.current = submitForm;
                            formikValuesRef.current = values;
                            return (
                                <Form>
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <TableContainer component={Paper}>
                                            <Table sx={{ minWidth: 800, tableLayout: 'fixed' }} aria-label="customized table">
                                                <TableHead>
                                                    <TableRow>
                                                        <StyledTableCell sx={{ width: 50 }} />
                                                        <StyledTableCell sx={{ width: '40%' }}>Product Name</StyledTableCell>
                                                        <StyledTableCell sx={{ width: '25%' }} align="center">Order Quantity</StyledTableCell>
                                                        <StyledTableCell sx={{ width: '35%' }} align="center">Process Quantity</StyledTableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    <InvoiceTableRows
                                                        orderItems={orderDetails.order_items || []}
                                                        values={values}
                                                        handleChange={handleChange}
                                                        setFieldValue={setFieldValue}
                                                        setFieldTouched={setFieldTouched}
                                                        selectedRowId={selectedRowId}
                                                        setBilledQuantities={setBilledQuantities}
                                                        handleSelectCheck={handleSelectCheck}
                                                    />
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Switch
                                                checked={checked}
                                                onChange={(e) => {
                                                    setChecked(e.target.checked);
                                                    if (!e.target.checked) {
                                                        setSelectedReason("");
                                                        setFieldValue("cancelReason", "");
                                                    }
                                                }}
                                            />
                                            <Typography>{checked ? "Cancellable" : "Is Cancellable"}</Typography>
                                        </Box>
                                        {checked && (
                                            <SelectFieldWithSearch
                                                name="cancelReason"
                                                label="Cancel Reason"
                                                placeholder="Select cancel reason"
                                                options={cancelReasons}
                                                value={values.cancelReason}
                                                isRequired
                                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                    setFieldValue("cancelReason", e.target.value);
                                                }}
                                                size="middle"
                                            />
                                        )}
                                    </Box>
                                </Form>
                            );
                        }}
                    </Formik>
                ) : (
                    <Typography>Loading...</Typography>
                )}
                onClose={() => {
                    setOpen(false)
                    navigate(location.pathname, { replace: true });
                }}
                onConfirm={() => submitFormRef.current && submitFormRef.current()}
                confirmText={checked ? "Cancel Order" : "Proceed"}
                confirmDisabled={!isProcessEnabled}
            />}
        </>
    );
};

export default InvoiceOptionsDialog;